import { useEffect, useMemo, useState } from "react";
import { IS_VATAR } from "../vatar/env";
import VatarShop from "../vatar/VatarShop";
import { Loader2 } from "lucide-react";
import useCoinStore from "../stores/coinStore";
import coinService from "../Services/coinService";
import authService from "../Services/authService";
// Naira goes through Paystack, USD through Bachs (see paymentService.js)
import { payForProduct } from "../Services/paymentService";
import { showPurchaseProblem } from "../Services/purchaseTracker";
import { paymentProviderFor } from "../Services/paymentRouting";
import usePaymentCurrency from "../hooks/usePaymentCurrency";
import usePayerDetails from "../hooks/usePayerDetails";
import { PRODUCTS, coinProductId, formatProductPrice, formatUsd } from "../Services/products";
import BottomSheet from "./BottomSheet";
import NameItCoin from "./NameItCoin";
import SubscriptionModal from "./SubscriptionModal";
import analytics from "../Services/analyticsService";
import { PRO_OFFER_COPY } from "../utils/hostingCopy";
import PayerDetails from "./payment/PayerDetails";
import CurrencyToggle from "./payment/CurrencyToggle";
import SignInGate from "./payment/SignInGate";

const PACKAGES = coinService.constructor.COIN_PACKAGES;
const PROVIDER_NAMES = { paystack: "Paystack", bachs: "Bachs" };

// The one coin store, opened from Home, rooms, results, power-ups and Account.
// `preset` comes from the mobile app's deep link: { coins, price, mobileRef }.
const CoinStore = ({ isOpen, onClose, preset = null }) => {
  const { coinBalance, syncBalance, isPro } = useCoinStore();
  const [showPro, setShowPro] = useState(false);
  const [chosenCurrency, setCurrency] = usePaymentCurrency();
  // Purchases handed off from the mobile app are always naira.
  const currency = preset?.mobileRef ? "NGN" : chosenCurrency;
  const [selectedCoins, setSelectedCoins] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState("");
  const [fallbackProvider, setFallbackProvider] = useState(null);
  const [canTryBachs, setCanTryBachs] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  // Reloads after signing in from the gate, so it reads the real account.
  const payer = usePayerDetails(isOpen && isSignedIn);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const user = authService.auth.currentUser;
    setIsSignedIn(!!(user && !user.isAnonymous));
    setError("");
    setCanTryBachs(false);
    setFallbackProvider(null);
    // The pack the mobile app already picked, otherwise the most popular one,
    // so the buyer starts one tap away from paying.
    const presetMatch = preset && PACKAGES.find((p) => p.coins === preset.coins && p.price === preset.price);
    setSelectedCoins((presetMatch || PACKAGES.find((p) => p.popular) || PACKAGES[0]).coins);
  }, [isOpen, preset]);

  const selected = PACKAGES.find((p) => p.coins === selectedCoins) || null;
  const provider = paymentProviderFor({ currency, mobileRef: preset?.mobileRef, fallbackProvider });

  // Savings against buying the same coins at the smallest pack's rate.
  const savingsLabel = useMemo(() => {
    const unit = (pkg) => (currency === "USD" ? PRODUCTS[coinProductId(pkg.coins)].priceUsdCents : pkg.price);
    const baseRate = Math.max(...PACKAGES.map((p) => unit(p) / p.coins));
    return (pkg) => {
      const saved = Math.round(pkg.coins * baseRate) - unit(pkg);
      if (saved <= 0) return null;
      return `Save ${currency === "USD" ? formatUsd(saved) : `₦${saved.toLocaleString()}`}`;
    };
  }, [currency]);

  const handleClose = () => {
    if (purchasing) return;
    onClose();
  };

  const pay = async (providerOverride = fallbackProvider) => {
    if (!selected || purchasing) return;
    setError("");
    setCanTryBachs(false);
    const details = await payer.commit();
    if (!details) return;

    setPurchasing(true);
    try {
      await payForProduct({
        currency,
        fallbackProvider: providerOverride,
        productId: coinProductId(selected.coins),
        email: details.email,
        name: details.name,
        mobileRef: preset?.mobileRef || null,
        label: "NameIt! Coins",
      });
      // Paystack finishes here and the status banner confirms it; Bachs has
      // already navigated to its hosted checkout.
      await syncBalance();
      onClose();
    } catch (err) {
      if (err.cancelled) return;
      if (err.reference) {
        // Money was taken: tell them at the top, with a way to reach us.
        showPurchaseProblem({
          message: "Your payment went through, but we couldn't confirm it yet. Your coins should arrive shortly; if they don't, email us.",
          reference: err.reference,
        });
        onClose();
        return;
      }
      const canFallback = currency === "NGN" && !preset?.mobileRef && providerOverride !== "bachs";
      setError(
        canFallback
          ? "Paystack didn't go through. You can try another payment method."
          : err.message || "We couldn't start the payment. Please try again."
      );
      setCanTryBachs(canFallback);
    } finally {
      setPurchasing(false);
    }
  };

  const tryBachs = () => {
    setFallbackProvider("bachs");
    pay("bachs");
  };

  const store = (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-brand-textSecondary">
          Balance <span className="font-semibold text-brand-text tabular-nums">{coinBalance.toLocaleString()}</span>
        </p>
        {!preset?.mobileRef && (
          <CurrencyToggle
            currency={currency}
            disabled={purchasing}
            onChange={(code) => {
              setCurrency(code);
              setFallbackProvider(null);
              setCanTryBachs(false);
              setError("");
            }}
          />
        )}
      </div>

      <div
        role="radiogroup"
        aria-label="Coin packs"
        className="rounded-xl border border-brand-border overflow-hidden divide-y divide-brand-borderLight"
      >
        {PACKAGES.map((pkg) => {
          const isSelected = pkg.coins === selectedCoins;
          const savings = savingsLabel(pkg);
          return (
            <button
              key={pkg.coins}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={purchasing}
              onClick={() => setSelectedCoins(pkg.coins)}
              className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-[background-color,box-shadow] focus-visible:outline-none focus-visible:bg-brand-accentMuted ${
                isSelected
                  ? "bg-brand-accentMuted shadow-[inset_0_0_0_2px_#dc2626] rounded-xl"
                  : "bg-brand-surface hover:bg-brand-surface2"
              }`}
            >
              {/* One pack per purchase: the outline and tint mark the choice. */}
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2">
                  <NameItCoin size="small" />
                  <span className="font-bold text-brand-text tabular-nums">{pkg.coins.toLocaleString()} coins</span>
                  {pkg.popular && (
                    <span className="text-[11px] font-semibold text-brand-accent bg-brand-surface border border-red-200 rounded-full px-2 py-px">
                      Popular
                    </span>
                  )}
                </span>
                {savings && <span className="block mt-0.5 ml-7 text-xs text-green-700">{savings}</span>}
              </span>
              <span className="font-bold text-brand-text tabular-nums">{formatProductPrice(coinProductId(pkg.coins), currency)}</span>
            </button>
          );
        })}
      </div>

      {!isPro && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
          <p className="font-bold text-brand-text">{PRO_OFFER_COPY.title}</p>
          <p className="mt-0.5 text-sm text-brand-textSecondary">{PRO_OFFER_COPY.body}</p>
          <button
            type="button"
            disabled={purchasing}
            onClick={() => {
              analytics.track("pro_upsell_opened", { source: "coin_store" });
              setShowPro(true);
            }}
            className="mt-3 h-10 px-5 rounded-lg bg-white border border-amber-300 text-amber-900 text-sm font-semibold hover:bg-amber-100 transition-colors"
          >
            {PRO_OFFER_COPY.button}
          </button>
        </div>
      )}

      {/* Sticky while the receipt is one line; while its fields are open it scrolls,
          so it never covers the packs. */}
      <div className={`${payer.editing ? "" : "sticky bottom-0 "}-mx-5 -mb-5 mt-5 px-5 pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] bg-brand-surface border-t border-brand-borderLight space-y-3`}>
        <PayerDetails payer={payer} disabled={purchasing} />

        {error && (
          <div className="rounded-lg bg-brand-accentMuted px-3 py-2.5 text-sm text-red-800" role="alert">
            <p>{error}</p>
            {canTryBachs && (
              <button
                type="button"
                onClick={tryBachs}
                disabled={purchasing}
                className="mt-1.5 font-semibold text-brand-accent underline underline-offset-2"
              >
                Pay with card instead
              </button>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => pay()}
          disabled={!selected || purchasing || !payer.loaded}
          className="w-full h-12 rounded-xl bg-brand-accent text-white font-semibold text-base flex items-center justify-center gap-2 transition-colors hover:bg-red-700 disabled:bg-gray-300 disabled:text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent"
        >
          {purchasing ? (
            <>
              <Loader2 size={18} className="animate-spin" aria-hidden="true" />
              Opening checkout…
            </>
          ) : selected ? (
            `Pay ${formatProductPrice(coinProductId(selected.coins), currency)}`
          ) : (
            "Choose a pack"
          )}
        </button>
        <p className="text-center text-xs text-brand-textSecondary">Secure checkout by {PROVIDER_NAMES[provider]}</p>
      </div>
    </>
  );

  return (
    <>
      <BottomSheet
        isOpen={isOpen}
        // Taps inside the sign-in dialog land outside the sheet; don't let them close it.
        onClose={authModalOpen ? () => {} : handleClose}
        title="Buy coins"
        subtitle="Use coins to add more players to your rooms"
      >
        {isSignedIn ? store : <SignInGate what="buy coins" onModalChange={setAuthModalOpen} onSignedIn={() => setIsSignedIn(true)} />}
      </BottomSheet>
      <SubscriptionModal isOpen={showPro} onClose={() => setShowPro(false)} />

    </>
  );
};

// Vatar builds sell through Vatar.purchase only (see src/vatar/VatarShop.jsx).
export default IS_VATAR ? VatarShop : CoinStore;
